/**
 * Holly API Service — client-side abstraction
 *
 * Calls the Koa gateway proxy at /hollyv4/chat which forwards to the
 * holly-v4-mcp `/orchestrate` endpoint (same V1 wire contract as v3). This keeps the backend URL server-side
 * and avoids CORS.
 *
 * `declaredIntent` is accepted for forward-compatibility / contract parity, but
 * note the gateway currently drops it before Holly — intent is carried by the
 * seed message text (the registration `initialMessage`).
 */

import type { HollyApiResponse } from './types';

const TIMEOUT_MS = 90_000;

export async function askHolly(
  message: string,
  threadId?: string | null,
  declaredIntent?: string | null
): Promise<HollyApiResponse> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const res = await fetch('/hollyv4/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message,
        ...(threadId ? { threadId } : {}),
        ...(declaredIntent ? { declaredIntent } : {}),
      }),
      signal: controller.signal,
    });

    if (!res.ok) {
      const errorBody = (await res.json().catch(() => ({}))) as {
        error?: string;
      };
      throw new Error(errorBody.error || `Holly returned ${res.status}`);
    }

    return (await res.json()) as HollyApiResponse;
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') {
      throw new Error('Holly took too long to respond. Please try again.');
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}
